/**
 * The middle house, measured off a street photograph.
 *
 * Proportions were read as fractions of the elevation and converted against
 * the 11.5 m frontage the street already assumes, so this house drops into
 * lot 1 without moving its neighbours, its driveway or its fences.
 *
 * Looking at the front from the road (+Z), screen-right is +X:
 *
 *      x -5.75 ......... -0.9 ..... 0.9 ......... +5.75
 *          |  PORCH BAY   | ENTRY  |  GARAGE BAY   |
 *          |  arched      | door + |  bay window   |
 *          |  window over | gable  |  over garage  |
 *          |  the porch   |        |               |
 *
 * The whole elevation is driven from FACE (the main brick plane).  Anything
 * that projects forward of it -- the porch, the entry gable, the bay window --
 * gives its own z explicitly.
 */

import { DECK } from "./backspec.js";
import { GRADE } from "../spec.js";

export const CX = 0;           // lot 1 centre
export const WALL_T = 0.30;    // brick wall thickness (shell builds to this,
                               // interior.js lines the inner face of it)
export const W = 11.5;         // frontage, matches HOUSE.width
export const D = 13;           // depth
export const FACE = 1.5;       // main brick plane, matches Z.houseFront
export const BACK = -11.5;

export const Y = {
  grade: 0,
  plinth: 0.55,        // brick base course the house sits on
  floor: 0.55,         // ground-floor level == porch deck
  storey: 3.35,        // first-floor ceiling / second-floor line
  eave: 6.30,          // main wall top
  ridge: 8.80,         // main roof ridge
  dormerApex: 9.55,    // the gable dormer over the centre
  porchEave: 3.30,     // underside line of the porch roof
  porchRidge: 3.95,    // where the porch roof meets the wall
  entryApex: 4.55,     // the little gable over the front door
};

export const X = {
  left: -5.75,
  right: 5.75,
  porchBayEnd: -0.90,     // porch bay / entry bay division
  entryBayEnd: 0.90,      // entry bay / garage bay division
  door: 0.0,              // front door centre -- dead centre of the elevation
  garage: 3.65,           // garage door centre (matches LOTS[1].house.garageX)
  archWindow: -3.80,      // the tall arched window, upper floor
  bayWindow: 3.50,        // the projecting bay over the garage
  dormer: -1.40,          // gable dormer, above and left of the door
};

export const PORCH = {
  minX: -5.50,
  maxX: 0.95,
  front: 3.70,            // porch deck front edge (z)
  deck: 0.55,             // deck top == floor level
  columnW: 0.26,
  railTop: 1.52,          // measured off the photo: chest-high on a person
  steps: 3,               // grade to deck, 0.183 rise each
};

export const ENTRY = {
  gableFront: 3.05,       // the entry gable projects less far than the porch
  gableW: 2.60,
};

/** The projecting bay window over the garage -- the detail to get right. */
export const BAY = {
  centreX: X.bayWindow,
  width: 3.00,            // wall opening it sits in
  project: 0.78,          // how far it juts out from FACE
  sill: 3.90,
  head: 5.55,
  capTop: 6.05,           // little hipped roof over the bay
  cheek: 0.55,            // angled side panes
};

export const GARAGE = { centreX: X.garage, width: 3.30, height: 2.32 };

/**
 * Every hole in the brick, as a rectangle on the FACE plane (or on a
 * projection's own plane).  `shell` builds masonry *around* these and leaves a
 * recess; `openings` fills them.  Both read this list, so a window can never
 * end up half-bricked-over.
 *
 * x, y are the centre; w, h the size; `plane` is the z the opening sits in.
 */
export const OPENINGS = [
  // --- upper floor ---
  // `through` on all four since the storey behind them was built: the same
  // rule the ground floor already followed -- a blind niche in a wall you can
  // stand behind reads as a mistake, and the arch is the stair landing's own
  // window, which would be a very odd thing to brick up from the inside.
  { id: "arch", kind: "arched", x: X.archWindow, y: 4.85, w: 2.55, h: 2.20,
    plane: FACE, arch: 0.62, through: true },     // Palladian: fanlight on top
  { id: "upperPairL", kind: "sash", x: -0.55, y: 4.75, w: 0.95, h: 1.55,
    plane: FACE, through: true },
  { id: "upperPairR", kind: "sash", x: 0.62, y: 4.75, w: 0.95, h: 1.55,
    plane: FACE, through: true },
  { id: "bay", kind: "bay", x: BAY.centreX, y: (BAY.sill + BAY.head) / 2,
    w: BAY.width, h: BAY.head - BAY.sill, plane: FACE, through: true },

  // --- ground floor ---
  // `through` cuts the masonry clean out instead of leaving a recess with a
  // brick back: there is a room behind these two now, so a backing panel
  // would be a brick wall hanging in the middle of the living room.
  { id: "livingRoom", kind: "sash", x: -3.60, y: 2.15, w: 2.60, h: 1.65, plane: FACE,
    through: true },
  { id: "frontDoor", kind: "door", x: X.door, y: 1.72, w: 1.05, h: 2.35, plane: FACE,
    through: true },
  { id: "garageDoor", kind: "garage", x: GARAGE.centreX,
    y: Y.grade + GARAGE.height / 2 + 0.05, w: GARAGE.width, h: GARAGE.height,
    plane: FACE },
];

export const opening = (id) => OPENINGS.find((o) => o.id === id);

/**
 * The windows on the three walls OPENINGS does not cover.
 *
 * Same contract as OPENINGS, one wall further round: shell.js cuts these and
 * glazes the outside; interior.js lines the inside of the ground-floor ones
 * and upstairs.js the upper ones.  `u` is x on the back wall and z on the two
 * sides; `y0`/`y1` are the head and sill heights.  All of them go `through`
 * now -- there is a room behind every one of them.
 */
export const SIDE_WINDOWS = [
  { wall: "back",  u: -3.20, w: 1.60, y0: 1.35, y1: 2.85, through: true },
  { wall: "back",  u: -0.30, w: 1.50, y0: 4.10, y1: 5.55, through: true },
  { wall: "back",  u:  3.40, w: 0.95, y0: 4.10, y1: 5.50, through: true },
  // -4.60 and not -3.20: upstairs this wall is the bathroom's, and at
  // -3.20 the window straddled the line between it and the front
  // bedroom.  Side fenestration is dressing this file invented, so it is
  // the cheaper of the two to move.
  { wall: "left",  u: -4.60, w: 1.00, y0: 4.05, y1: 5.50, through: true },
  { wall: "left",  u: -7.60, w: 1.00, y0: 4.05, y1: 5.50, through: true },
  { wall: "left",  u: -5.40, w: 1.30, y0: 1.50, y1: 3.00, through: true },
  // -2.30 and not -3.00: at -3.00 this window's last 0.10 m fell the far
  // side of an upstairs partition -- a wall dying into a window reveal,
  // which reads as a mistake from both rooms.  The side and back
  // fenestration is dressing this file invented rather than anything the
  // photograph shows, so it is the cheaper of the two to move.  It now sits
  // squarely in UPPER's east bedroom.
  { wall: "right", u: -2.30, w: 1.00, y0: 4.05, y1: 5.50, through: true },
  { wall: "right", u: -8.20, w: 1.00, y0: 4.05, y1: 5.50, through: true },
  { wall: "right", u: -9.80, w: 1.15, y0: 1.60, y1: 3.00, through: true },
];

/** Those windows as {u0,u1,y0,y1} rectangles, for whichever wall. */
export const sideWindows = (wall) =>
  SIDE_WINDOWS.filter((w) => w.wall === wall).map((w) => ({
    u0: w.u - w.w / 2, u1: w.u + w.w / 2, y0: w.y0, y1: w.y1,
    through: w.through === true,
  }));

/** Colours, as keys into the shared MATS registry. */
export const LOOK = {
  brick: "brickWarm",       // light tan, as photographed
  trim: "trim",             // cream/white fascia, columns, window surrounds
  trimShade: "trimShade",
  roof: "shingle",          // charcoal asphalt
  door: "doorRed",          // the red front door
  garageDoor: "garageDoor",  // white sectional door (the red one was overruled)
  glass: "glass",
  stone: "stoneTrim",
  rail: "trim",
};

/**
 * The ground storey.
 *
 * The floor is the front doorstep's level and the ceiling is the first-floor
 * line, so the inside needs no numbers of its own: it is the volume the shell
 * already encloses, minus the 0.30 m of brick.  The storey above it is UP,
 * further down this file; INSIDE.ceiling is the plane they share.
 */
export const INSIDE = {
  floor: Y.floor,               // 0.55, level with the porch deck
  ceiling: Y.storey,            // 3.35
  wallT: 0.12,                  // partition thickness
  minX: X.left + WALL_T,        // -5.45   inner faces of the brick
  maxX: X.right - WALL_T,       //  5.45
  minZ: BACK + WALL_T,          // -11.20
  maxZ: FACE - WALL_T,          //   1.20
};

/**
 * The sliding patio door in the back wall, onto the deck.
 *
 * You step straight out onto the deck: its sill is the floor.  That works
 * because the back garden is a terrace 0.80 m below the street (spec.GRADE),
 * so backspec's deck -- 1.35 m above its own lawn, as photographed -- tops
 * out exactly level with the kitchen.  The two numbers are written as that
 * subtraction rather than as 0.55, so neither can drift from the other.
 */
export const BACK_DOOR = {
  x: 3.20,                      // over the middle of the deck (0.55..5.60)
  w: 1.80,                      // two 0.90 leaves; the -X one is slid open
  sill: DECK.top - GRADE.drop,  // 0.55, and Y.floor says the same
  head: DECK.top - GRADE.drop + 2.05,
  plane: BACK,
  openTo: 3.20,                 // clear opening runs x 2.30 .. here
};

if (Math.abs(BACK_DOOR.sill - Y.floor) > 1e-6) {
  console.warn(`hero: the deck lands at ${BACK_DOOR.sill.toFixed(2)} but the ` +
    `floor is at ${Y.floor} -- the patio door needs steps again, or one of ` +
    `DECK.top / GRADE.drop / Y.floor has moved without the others`);
}

/** The front walkway, which hero owns for this lot. */
export const WALK = {
  x: X.door,
  width: 1.30,
  fromZ: 3.70,      // porch steps
  toZ: 15.0,        // meets the sidewalk
};

/** The maple in the front lawn, called out in the reference. */
export const TREE = {
  x: -1.9, z: 8.6,
  ringR: 1.60,        // wide enough for the tree and the shrub beside it
  height: 7.2,
  trunkR: 0.17,
  forkY: 1.65,        // where the single trunk breaks into limbs
  crownBase: 2.25,    // lowest foliage -- well down the trunk, not a lollipop
};

// ===========================================================================
// The upper storey
// ===========================================================================
/**
 * The floor the shell always had room for and nobody had built.
 *
 * `INSIDE` stops at Y.storey because that used to be the lid of the world:
 * the ceiling closed the house off and the brick above it enclosed nothing.
 * It encloses a storey now.  The numbers are the same volume seen from the
 * other side -- the deck sits ON INSIDE.ceiling and the ceiling goes up to
 * the eave, which is where the shell's brick already stops.
 *
 *   3.35  INSIDE.ceiling   the plaster the ground floor looks up at
 *   3.50  UP.floor         the boards you walk on -- joists in between
 *   6.30  UP.ceiling       Y.eave, the top of the brickwork
 *
 * 0.15 m of structure between the two is thin for a real floor and exactly
 * right here: the stair has to climb it, and every 10 cm of it is another
 * riser or a steeper one.
 */
export const UP = {
  soffit: Y.storey,             // 3.35, the room below's ceiling
  floor: Y.storey + 0.15,       // 3.50, the walking surface
  // Y.eave less 0.08, not Y.eave.  The shell's roof furniture is bedded
  // 0.02 into the wall top so none of it lands coplanar with the brick --
  // the dormer's two brick cheeks start at 6.28 -- and a ceiling AT the eave
  // left them hanging 2 cm below it, reading from the landing as two dark
  // beams across the plaster.  Dropping the ceiling 8 cm puts the whole of
  // that furniture above it and still leaves a 2.72 m room, 0.27 m of it
  // clear above the arch window's head.
  ceiling: Y.eave - 0.08,       // 6.22
  wallT: INSIDE.wallT,          // 0.12, same partitions as downstairs
  minX: INSIDE.minX,            // -5.45   the brick's inner faces, unchanged:
  maxX: INSIDE.maxX,            //  5.45   the same shell encloses both floors
  minZ: INSIDE.minZ,            // -11.20
  maxZ: INSIDE.maxZ,            //   1.20
};

/**
 * The ground floor, from the client's drawing.
 *
 * One wall runs the whole depth of the house and everything else hangs off
 * it: living-and-dining to the west of it, kitchen-and-garage to the east.
 * There is no central hall -- the front door opens straight into the dining
 * end, and the stair climbs the east face of that same wall.
 *
 *      z-11.20 +-----------------+----------------------+
 *              |                 |[  open  ]            |
 *              |  LIVING         |          KITCHEN     |
 *              |                 |                      |
 *      z -6.20 +- - - - - - - - -|                      |
 *              |                 +----------------------+  kitZ
 *              |  DINING         |                      |
 *              |          stair->|          GARAGE      |
 *              |          [door] |                      |
 *      z  1.20 +----[ DOOR ]-----+----------------------+
 *             x -5.45         1.75                    5.45
 *
 * The living/dining line is drawn as two stubs with the middle open, which
 * is what the plan shows: one room that narrows rather than two rooms with a
 * door between them.  `open` is the gap in the long wall that puts the
 * kitchen and the living end into one space at the back.
 *
 * The garage door has to sit in the 1.46 m between the stair's landing and
 * the closet -- north of the flight, because under the flight at that point
 * there is 1.9 m of headroom and a door needs 2.6.
 */
export const PLAN = {
  divX: 1.75,                       // the long wall, and the stair's back
  // -6.20 and not the -5.60 the drawing suggests: the west wall's ground
  // window spans z -6.05..-4.75, and at -5.60 this line stood in the
  // middle of it.  The drawing's own note was that its dimensions are
  // off, so the line moves and the window stays where the elevation put it.
  livZ: -6.20,                      // living (back) / dining (front)
  kitZ: -4.60,                      // kitchen (back) / garage (front)
  livStubs: [[-5.45, -3.20], [-1.20, 0.45]],   // what is left of that line
  open: { z0: -10.90, z1: -7.55 },  // living <-> kitchen, through divX
  garageDoor: { z: -1.10, w: 0.90 },
};

/**
 * The dog-leg stair, drawn from the client's own floor plan.
 *
 * It climbs the GARAGE WALL -- the full-depth partition at PLAN.divX that
 * splits living-and-dining from kitchen-and-garage -- running from the front
 * of the house toward the back.  That wall is the one it should always have
 * used: it is full height, it is already there, and it is the only wall in
 * the plan long enough to take a flight without one being invented.
 *
 * Fifteen risers of 0.197 carry 0.55 to 3.50.  Not a taste call: player.js
 * steps up 0.23 m without being asked to jump, so a riser over that is a
 * staircase the cat has to pogo up, and every other stair in the level is
 * built to 0.183-0.217 for the same reason.
 *
 * Plan, seen from the street (so +X is to the right, as it is on the
 * elevation, and the front door is at the bottom):
 *
 *      z -6.08  +-------------+  head, on to the boards
 *               |             |
 *               |  11 treads  |  climbing -Z with the garage wall at
 *               |             |  their back
 *      z -3.00  +----+---+----+
 *               | 2  | 1 | L  |  three risers, turning right off the hall
 *      z -1.76  +----+---+----+
 *               |   [ GARAGE DOOR ]
 *               |             +---------+
 *               |             | CLOSET  |  mirrored sliders, first thing
 *      z  1.20  |   [ DOOR ]  +---------+  on your right
 *              x -0.11  0.45  1.10  1.69
 *
 * You come in, cross 2.96 m of hall, take three steps up to your right on to
 * the quarter landing, turn left, and the flight runs away from you up the
 * wall.  The three steps are what the plan draws swinging out into the hall,
 * and they are what makes this a dog-leg rather than a straight run.
 *
 * The flight's -X side is open to the dining room and carries the
 * balustrade; its +X side is the garage wall, so unlike the first attempt at
 * this stair there is no wall to invent for it to run along.
 */
const STAIR_RISERS = 15;
const STAIR_RISE = (UP.floor - INSIDE.floor) / STAIR_RISERS;   // 0.196667
const STAIR_GOING = 0.28;
const STAIR_WIDE = 1.24;
const FLIGHT_X1 = 1.69;                          // against the garage wall
const FLIGHT_X0 = FLIGHT_X1 - STAIR_WIDE;        // 0.45, the open side
const FLIGHT_NOSE = -3.00;                       // z of the first tread

export const STAIR = {
  risers: STAIR_RISERS,
  rise: STAIR_RISE,
  going: STAIR_GOING,
  wide: STAIR_WIDE,
  bottom: INSIDE.floor,           // 0.55
  top: UP.floor,                  // 3.50

  /** The three risers off the hall: two treads climbing +X, then the landing. */
  low: {
    z0: FLIGHT_NOSE, z1: FLIGHT_NOSE + STAIR_WIDE,     // -3.00 .. -1.76
    nose: FLIGHT_X0 - 2 * STAIR_GOING,                 // -0.11, tread 1's nosing
    treads: 2,                    // the third riser lands on `landing`
  },

  /** Quarter-space landing, three risers up, at the foot of the flight. */
  landing: {
    x0: FLIGHT_X0, x1: FLIGHT_X1,
    z0: FLIGHT_NOSE, z1: FLIGHT_NOSE + STAIR_WIDE,
    y: INSIDE.floor + 3 * STAIR_RISE,                  // 1.14
  },

  /** The straight flight: eleven treads climbing -Z, then one last riser. */
  flight: {
    x0: FLIGHT_X0, x1: FLIGHT_X1,
    nose: FLIGHT_NOSE,            // z of tread 1's nosing
    treads: 11,
    y0: INSIDE.floor + 3 * STAIR_RISE,                 // 1.14, off the landing
    head: FLIGHT_NOSE - 11 * STAIR_GOING,              // -6.08
  },

  /**
   * The wall the flight runs along -- the plan's own, not one of ours.
   * Kept as an export because stairs.js still wants to know which side its
   * balustrade is NOT on, and interior.js has to leave the run of wall
   * behind the flight unbroken.
   */
  wall: { at: PLAN.divX, side: 1 },
};

/**
 * The hole the stair comes up through: the flight's plan exactly.
 *
 * The landing keeps a ceiling over it -- 2.36 m of headroom, more than the
 * rooms have -- and the opening starts where the flight starts.  upstairs.js
 * cuts this out of the floor, its collider and its ceiling rim; interior.js
 * cuts it out of the ground floor's ceiling.  One rectangle, four consumers.
 */
export const STAIRWELL = {
  x0: FLIGHT_X0, x1: FLIGHT_X1,                        //  0.45 ..  1.69
  z0: STAIR.flight.head, z1: FLIGHT_NOSE,              // -6.08 .. -3.00
};

/**
 * The mirrored sliding closet, first thing on your right.
 *
 * A shallow box against the garage wall between the front elevation and the
 * garage doorway -- 0.59 m of carcass, which is a coat's shoulders and no
 * more.  Two 0.73 m leaves in two tracks; the back one is slid clear so
 * there is a gap standing open, because a closet a cat cannot get into is a
 * wall with a mirror on it.
 */
export const CLOSET = {
  x0: 1.10,                       // the leaf plane, facing the hall
  x1: FLIGHT_X1,                  // carcass back, against the partition
  z0: -0.30, z1: 1.16,
  floor: INSIDE.floor,            // 0.55
  head: 2.20,                     // top of the door opening
  top: Y.storey,                  // bulkhead runs to the ceiling
  leaf: 0.73,
  slide: 0.50,                    // how far the back leaf is pushed open
  shelf: 1.85,
  rail: 1.72,
};

/**
 * The rooms upstairs, from the client's second drawing.
 *
 * Same organising idea as the floor below: the long wall runs the depth of
 * the house, and the stair climbs beside it.  The rooms sit along the west
 * wall with a corridor between them and the well, and the two rooms east of
 * the long wall are reached one from the master and one from the landing.
 *
 *      z-11.20 +--------------------------+---------------+
 *              |  MASTER BEDROOM          |   EN-SUITE    |
 *              |  left -7.60, back -0.30  |  back 3.40    |
 *              |                          |  right -8.20  |
 *      z -7.00 +-------------+-----+------+---------------+
 *              |  BATHROOM   |  C  |[well]|   BEDROOM     |
 *              |  left -4.60 |  O  |      |   the bay     |
 *              |             |  R  |      |   right -2.30 |
 *      z -2.60 +-------------+-----+------+               |
 *              |  BEDROOM          | DESK |               |
 *              |  the arch         | pair |               |
 *              |  + pair L         |  R   |               |
 *      z  1.20 +-------------------+------+---------------+
 *             x -5.45          -0.05  0.45 1.75         5.45
 *
 * Every one of the ten openings the shell cuts lands in exactly one room,
 * which is the constraint the plan was fitted to: an upstairs room with no
 * window is a cupboard, and a landing with four is a corridor.
 *
 * The en-suite is reached only through the master, as the drawing has it --
 * its own walls face the master on one side and the front bedroom on the
 * other, so there is nowhere else for its door to go.  The front-east
 * bedroom's door cannot sit where the drawing puts it, beside the flight,
 * because upstairs that is the open well; it moves north of the well instead
 * and opens off the desk nook.
 */
export const UPPER = {
  // The corridor runs from the west rooms right up to the long wall and
  // the WELL IS A HOLE IN IT, rather than stopping at the well's edge --
  // otherwise the 0.30 m strip between the well and the wall, and the
  // arrival strip you step off the top tread on to, belong to no room at
  // all and nothing lays a floor or a skirting on them.
  corrX0: -1.00, corrX1: PLAN.divX,            // -1.00 .. 1.75, round the well
  masterZ: -7.00,                 // master / corridor + bathroom
  frontZ: -2.60,                  // corridor + bathroom / the front rooms
  // -0.05, hard against the east jamb of the front pair's west light
  // (-0.075): at -0.20 the partition stood in the middle of that window.
  deskX: -0.05,                   // front bedroom / desk nook
  eastZ: -6.93,                   // en-suite (back) / front-east bedroom
  ensuiteDoor: { z: -9.00, w: 0.95 },     // through divX, off the master
  eastDoor: { z: -2.20, w: 0.95 },        // through divX, off the desk nook
  /** Built-in wardrobes, as drawn: {x0,x1,z0,z1} against the west wall. */
  // Both of these were wrong where I first drew them, and both faults were
  // the same fault: a rectangle typed against the drawing without checking
  // what it landed on.
  //
  // The master's robe sat at z -7.60..-7.00, which is half inside the west
  // wall's own window (-8.10..-7.10) -- a wardrobe bulkhead across the top
  // third of a window.  It goes in the back-west corner instead, hard
  // against two walls and clear of everything.
  //
  // The second sat at z -3.20..-2.60, which is 0.60 m the BATHROOM side of
  // `frontZ` while its comment claimed the front bedroom.  The drawing puts
  // that wardrobe at the bedroom's north end, so it moves rather than the
  // label.
  robes: [
    { x0: UP.minX, x1: -2.70, z0: UP.minZ, z1: UP.minZ + 0.60 },  // master
    { x0: UP.minX, x1: -2.70, z0: -2.60, z1: -2.00 },   // front bedroom
  ],
  /** The desk under the front-east window, and a fair cat perch at 0.78. */
  desk: { x0: 0.10, x1: 1.60, z0: 0.30, z1: 0.90, top: 0.78 },
};
